import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { Line2d } from '@/editor/math/Line2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { sortConnectedLinesClockwise } from '@/editor/math/utils';
import { ConnectRelateWallInfo, SpecificConnectRelateWallInfo, WallData } from './Wall';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { getUniqueId } from '@/editor/utils';
import { LineWall } from '../type';
import { equalsWallData } from '@/editor/utils/wall';
import { WallBaseData } from './wallBaseData';
import { getBaseShape } from '@/editor/utils/wallShape';

export function initDrawWallInfo(baseData: WallBaseData) {
  const { baseShape, startShape, endShape } = getBaseShape(baseData);

  return {
    baseShape: baseShape,
    startShape: startShape,
    endShape: endShape,
    drawShape: [baseShape]
  };
}

export function findIndexWallData(mainWallData: WallData, wallDatas: WallData[]) {
  const index = wallDatas.findIndex((wallData) => equalsWallData(wallData, mainWallData));
  return index;
}

export function rejectWallData(wallDatas: WallData[], rejectWallDatas: WallData[]) {
  const resultWallDatas: WallData[] = [];
  for (const wallData of wallDatas) {
    let hasStartEnd = false;
    for (const startConnectWall of rejectWallDatas) {
      if (equalsWallData(startConnectWall, wallData)) {
        hasStartEnd = true;
        break;
      }
    }

    if (!hasStartEnd) {
      resultWallDatas.push(wallData);
    }
  }

  return resultWallDatas;
}

export function wallDatasToLineSegs(wallDatas: WallData[]): LineWall[] {
  const lineWalls: LineWall[] = [];
  wallDatas.forEach((wall) => {
    const { baseData } = wall;
    const lineSeg = new LineSeg2d(Point2dUtils.clone(baseData.start), Point2dUtils.clone(baseData.end));
    lineWalls.push({
      lineSeg,
      thickness: baseData.thickness,
      height: baseData.height,
      offGround: baseData.offGround
    });
  });

  return lineWalls;
}

export function wallToString(wallData: WallData): string {
  const { baseData } = wallData;
  return `${baseData.start.x.toFixed(6)},${baseData.start.y.toFixed(6)}|${baseData.end.x.toFixed(6)},${baseData.end.y.toFixed(6)}|
    ${baseData.thickness.toFixed(6)}|${baseData.height.toFixed(6)}|${baseData.offGround}`;
}

// 获取连接搭接的墙体
export function getConnectLapWalls(mainWallData: WallData, wallDatas: WallData[]) {
  const mainBaseData = mainWallData.baseData;
  const mainWallSeg = new LineSeg2d(mainBaseData.start, mainBaseData.end);

  const startConnectWalls: WallData[] = [];
  const endConnectWalls: WallData[] = [];
  const otherConnectWalls: WallData[] = [];
  for (const wallData of wallDatas) {
    if (equalsWallData(wallData, mainWallData)) {
      continue;
    }

    const baseData = wallData.baseData;
    const wallSeg = new LineSeg2d(baseData.start, baseData.end);
    if (wallSeg.pointOnSegment(mainBaseData.start)) {
      startConnectWalls.push(wallData);
    }

    if (wallSeg.pointOnSegment(mainBaseData.end)) {
      endConnectWalls.push(wallData);
    }

    const startIndex = findIndexWallData(wallData, startConnectWalls);
    const endIndex = findIndexWallData(wallData, endConnectWalls);
    if (startIndex === -1 && endIndex === -1 && mainWallSeg.intersection(wallSeg)) {
      otherConnectWalls.push(wallData);
    }
  }

  return { startConnectWalls, endConnectWalls, otherConnectWalls };
}

// 获取相交的墙体
export function getIntersectWalls(mainWallData: WallData, wallDatas: WallData[]) {
  const mainBaseData = mainWallData.baseData;
  const mainWallSeg = new LineSeg2d(mainBaseData.start, mainBaseData.end);

  const intersectWalls: WallData[] = [];
  for (const wallData of wallDatas) {
    const wallSeg = new LineSeg2d(wallData.baseData.start, wallData.baseData.end);
    if (equalsWallData(wallData, mainWallData) || mainWallSeg.isParallel(wallSeg)) {
      continue;
    }

    if (mainWallSeg.intersection(wallSeg)) {
      intersectWalls.push(wallData);
    }
  }

  return intersectWalls;
}

export function getStartEndConnectWalls(mainWallData: WallData, wallDatas: WallData[]) {
  const mainBaseData = mainWallData.baseData;
  const startConnectWalls: WallData[] = [];
  const endConnectWalls: WallData[] = [];
  for (const wallData of wallDatas) {
    if (equalsWallData(wallData, mainWallData)) {
      continue;
    }

    const baseData = wallData.baseData;
    if (
      Point2dUtils.equals(mainBaseData.start, baseData.start) ||
      Point2dUtils.equals(mainBaseData.start, baseData.end)
    ) {
      startConnectWalls.push(wallData);
    }

    if (Point2dUtils.equals(mainBaseData.end, baseData.start) || Point2dUtils.equals(mainBaseData.end, baseData.end)) {
      endConnectWalls.push(wallData);
    }
  }

  return { startConnectWalls, endConnectWalls };
}

export function getStartEndConnetWallInfos(mainWallData: WallData, wallDatas: WallData[]) {
  const mainBaseData = mainWallData.baseData;
  const startConnectWallInfos: ConnectRelateWallInfo[] = [];
  const endConnectWallInfos: ConnectRelateWallInfo[] = [];
  for (const wallData of wallDatas) {
    if (equalsWallData(wallData, mainWallData)) {
      continue;
    }

    const baseData = wallData.baseData;
    if (
      Point2dUtils.equals(mainBaseData.start, baseData.start) ||
      Point2dUtils.equals(mainBaseData.start, baseData.end)
    ) {
      startConnectWallInfos.push({
        wallData,
        connectPoint: Point2dUtils.clone(mainBaseData.start)
      });
    }

    if (Point2dUtils.equals(mainBaseData.end, baseData.start) || Point2dUtils.equals(mainBaseData.end, baseData.end)) {
      endConnectWallInfos.push({ wallData, connectPoint: Point2dUtils.clone(mainBaseData.end) });
    }
  }

  return { startConnectWallInfos, endConnectWallInfos };
}

export function getAllConnectWallInfos(mainWallData: WallData, wallDatas: WallData[]): ConnectRelateWallInfo[] {
  const mainBaseData = mainWallData.baseData;
  const connectWallinfos: ConnectRelateWallInfo[] = [];
  const mainWallSeg = new LineSeg2d(Point2dUtils.clone(mainBaseData.start), Point2dUtils.clone(mainBaseData.end));
  for (const wallData of wallDatas) {
    if (equalsWallData(mainWallData, wallData)) {
      continue;
    }

    const baseData = wallData.baseData;
    const wallSeg = new LineSeg2d(Point2dUtils.clone(baseData.start), Point2dUtils.clone(baseData.end));
    const intersectPoint = mainWallSeg.intersection(wallSeg);
    if (intersectPoint) {
      connectWallinfos.push({ wallData, connectPoint: intersectPoint });
    } else if (
      Point2dUtils.equals(mainBaseData.start, baseData.start) ||
      Point2dUtils.equals(mainBaseData.start, baseData.end)
    ) {
      connectWallinfos.push({ wallData, connectPoint: mainBaseData.start });
    } else if (
      Point2dUtils.equals(mainBaseData.end, baseData.start) ||
      Point2dUtils.equals(mainBaseData.end, baseData.end)
    ) {
      connectWallinfos.push({ wallData, connectPoint: mainBaseData.end });
    }
  }

  return connectWallinfos;
}

function getWallThicknesses(mainWallData: WallData, connectLineSegs: LineSeg2d[], connectWalls: WallData[]) {
  const thicknesses: number[] = [];
  for (const connectLineSeg of connectLineSegs) {
    let hasEqual = false;
    for (const connectWall of connectWalls) {
      if (
        Point2dUtils.equals(connectWall.baseData.start, connectLineSeg.start) &&
        Point2dUtils.equals(connectWall.baseData.end, connectLineSeg.end)
      ) {
        thicknesses.push(connectWall.baseData.thickness);
        hasEqual = true;
        break;
      }
    }

    if (!hasEqual) {
      thicknesses.push(mainWallData.baseData.thickness);
    }
  }

  return thicknesses;
}

export function calWallOutline(mainWallData: WallData, wallDatas: WallData[]) {
  const mainBaseData = mainWallData.baseData;
  const { startConnectWalls, endConnectWalls } = getStartEndConnectWalls(mainWallData, wallDatas);
  const mainLineSeg = new LineSeg2d(Point2dUtils.clone(mainBaseData.start), Point2dUtils.clone(mainBaseData.end));

  const startTempConnectLineSegs: LineSeg2d[] = [];
  for (const startConnectWall of startConnectWalls) {
    const startBaseData = startConnectWall.baseData;
    startTempConnectLineSegs.push(
      new LineSeg2d(Point2dUtils.clone(startBaseData.start), Point2dUtils.clone(startBaseData.end))
    );
  }
  const startConnectLineSegs: LineSeg2d[] = startTempConnectLineSegs.length
    ? sortConnectedLinesClockwise(mainLineSeg, startTempConnectLineSegs, true)
    : [];

  const startThicknesses = getWallThicknesses(mainWallData, startConnectLineSegs, startConnectWalls);

  const endTempConnectLineSegs: LineSeg2d[] = [];
  for (const endConnectWall of endConnectWalls) {
    const endBaseData = endConnectWall.baseData;
    endTempConnectLineSegs.push(
      new LineSeg2d(Point2dUtils.clone(endBaseData.start), Point2dUtils.clone(endBaseData.end))
    );
  }
  const endConnectLineSegs: LineSeg2d[] = endTempConnectLineSegs.length
    ? sortConnectedLinesClockwise(mainLineSeg, endTempConnectLineSegs, false)
    : [];

  const endThicknesses = getWallThicknesses(mainWallData, endConnectLineSegs, endConnectWalls);

  const { mainLeft: mainStartLeft, mainRight: mainStartRight } = calLeftRightIntersection(
    mainWallData,
    mainLineSeg,
    startConnectLineSegs,
    startThicknesses,
    true
  );
  const { mainLeft: mainEndLeft, mainRight: mainEndRight } = calLeftRightIntersection(
    mainWallData,
    mainLineSeg,
    endConnectLineSegs,
    endThicknesses,
    false
  );

  return [
    mainStartRight,
    Point2dUtils.clone(mainBaseData.start),
    mainStartLeft,
    mainEndLeft,
    Point2dUtils.clone(mainBaseData.end),
    mainEndRight
  ];
}

function calLeftRightIntersection(
  mainWallData: WallData,
  mainLineSeg: LineSeg2d,
  connectLineSegs: LineSeg2d[],
  thicknesses: number[],
  isStart: boolean
) {
  const mainBaseData = mainWallData.baseData;
  const { leftCurve, rightCurve } = Curve2dUtils.offsetCurves(mainLineSeg, mainBaseData.thickness / 2);
  const mainLeftLine = new Line2d({
    points: { p1: Point2dUtils.clone(leftCurve.start), p2: Point2dUtils.clone(leftCurve.end) }
  });
  const mainRightLine = new Line2d({
    points: { p1: Point2dUtils.clone(rightCurve.start), p2: Point2dUtils.clone(rightCurve.end) }
  });

  const { left, right } = Curve2dUtils.offsetPoints(
    mainLineSeg,
    isStart ? Point2dUtils.clone(mainLineSeg.start) : Point2dUtils.clone(mainLineSeg.end),
    mainBaseData.thickness / 2
  );
  let mainLeft: IPoint2d | undefined, mainRight: IPoint2d | undefined;
  if (connectLineSegs.length === 0) {
    mainLeft = left;
    mainRight = right;
  } else {
    const len = connectLineSegs.length;
    // 如果不是首尾相连要重构线段用于计算交点（目的是为了让绕向保持一致，也就是左右偏移线可以对应上）
    const firstConnectLineSeg = isStart
      ? Point2dUtils.equals(mainLineSeg.start, connectLineSegs[0].end)
        ? connectLineSegs[0]
        : new LineSeg2d(connectLineSegs[0].end, connectLineSegs[0].start)
      : Point2dUtils.equals(mainLineSeg.end, connectLineSegs[0].start)
        ? connectLineSegs[0]
        : new LineSeg2d(connectLineSegs[0].end, connectLineSegs[0].start);
    const lastConnectLineSeg = isStart
      ? Point2dUtils.equals(mainLineSeg.start, connectLineSegs[len - 1].end)
        ? connectLineSegs[len - 1]
        : new LineSeg2d(connectLineSegs[len - 1].end, connectLineSegs[len - 1].start)
      : Point2dUtils.equals(mainLineSeg.end, connectLineSegs[len - 1].start)
        ? connectLineSegs[len - 1]
        : new LineSeg2d(connectLineSegs[len - 1].end, connectLineSegs[len - 1].start);

    // 起点的右边让firstLineSeg的又偏移线计算交点
    const firstLineInfo = Curve2dUtils.offsetCurves(firstConnectLineSeg, thicknesses[0] / 2);
    const firstLeftLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(firstLineInfo.leftCurve.start),
        p2: Point2dUtils.clone(firstLineInfo.leftCurve.end)
      }
    });
    const firstRightLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(firstLineInfo.rightCurve.start),
        p2: Point2dUtils.clone(firstLineInfo.rightCurve.end)
      }
    });
    // 起点的右边让lastLineSeg的又偏移线计算交点
    const lastLineInfo = Curve2dUtils.offsetCurves(lastConnectLineSeg, -thicknesses[len - 1] / 2);
    const lastLeftLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(lastLineInfo.leftCurve.start),
        p2: Point2dUtils.clone(lastLineInfo.leftCurve.end)
      }
    });
    const lastRightLine = new Line2d({
      points: {
        p1: Point2dUtils.clone(lastLineInfo.rightCurve.start),
        p2: Point2dUtils.clone(lastLineInfo.rightCurve.end)
      }
    });

    const leftIntersectLine = isStart ? lastLeftLine : firstLeftLine;
    const rightIntersectLine = isStart ? firstRightLine : lastRightLine;

    const leftIntersect = mainLeftLine.isParallelTo(leftIntersectLine)
      ? left
      : mainLeftLine.intersection(leftIntersectLine);
    const rightIntersect = mainRightLine.isParallelTo(rightIntersectLine)
      ? right
      : mainRightLine.intersection(rightIntersectLine);

    mainLeft = leftIntersect ? leftIntersect : left;
    mainRight = rightIntersect ? rightIntersect : right;
  }

  return { mainLeft, mainRight };
}

// 连接的墙体
function pointConnectRelateWalls(mainWallData: WallData, point: IPoint2d, wallDatas: WallData[]) {
  const connectWallDataInfos: ConnectRelateWallInfo[] = [];
  for (const wallData of wallDatas) {
    if (equalsWallData(mainWallData, wallData)) {
      continue;
    }

    const { baseData } = wallData;
    if (Point2dUtils.equals(point, baseData.start) || Point2dUtils.equals(point, baseData.end)) {
      connectWallDataInfos.push({ wallData, connectPoint: point });
    }
  }

  return connectWallDataInfos;
}

// 搭接的墙体
function pointLapRelateWalls(mainWallData: WallData, point: IPoint2d, wallDatas: WallData[]) {
  const lapWallDataInfos: ConnectRelateWallInfo[] = [];
  for (const wallData of wallDatas) {
    if (equalsWallData(mainWallData, wallData)) {
      continue;
    }

    // 连接的跳过，不能算搭接
    if (Point2dUtils.equals(point, wallData.baseData.start) || Point2dUtils.equals(point, wallData.baseData.end)) {
      continue;
    }

    const wallSeg = new LineSeg2d(
      Point2dUtils.clone(wallData.baseData.start),
      Point2dUtils.clone(wallData.baseData.end)
    );

    if (wallSeg.pointOnSegment(point)) {
      lapWallDataInfos.push({ wallData, connectPoint: point });
    }
  }

  return lapWallDataInfos;
}

// 起始点连接在主墙体上的关联墙体
export function connectRelateWallInfo(mainWallData: WallData, wallDatas: WallData[]) {
  const mainBaseData = mainWallData.baseData;
  const mainWallSeg = new LineSeg2d(Point2dUtils.clone(mainBaseData.start), Point2dUtils.clone(mainBaseData.end));

  const relateWallDataInfos: ConnectRelateWallInfo[] = [];
  // 先获取与主墙体起始点连接的墙体（有且只有一堵的时候才能判断为关联墙体)
  const startConnectWallDataInfos = pointConnectRelateWalls(mainWallData, mainBaseData.start, wallDatas);
  const endConnectWallDataInfos = pointConnectRelateWalls(mainWallData, mainBaseData.end, wallDatas);

  const startLapWallDataInfos = pointLapRelateWalls(mainWallData, mainBaseData.start, wallDatas);
  const endLapWallDataInfos = pointLapRelateWalls(mainWallData, mainBaseData.end, wallDatas);

  const startOnlyConnectRelateWallInfo =
    startConnectWallDataInfos.length === 1 ? startConnectWallDataInfos[0] : undefined;
  const endOnlyConnectRelateWallInfo = endConnectWallDataInfos.length === 1 ? endConnectWallDataInfos[0] : undefined;

  for (const wallData of wallDatas) {
    // 自身还有起始点相连的不参与之后的计算
    if (equalsWallData(mainWallData, wallData)) {
      continue;
    }

    const { baseData } = wallData;
    if (mainWallSeg.pointOnSegment(baseData.start)) {
      if (
        (startOnlyConnectRelateWallInfo && equalsWallData(wallData, startOnlyConnectRelateWallInfo.wallData)) ||
        (endOnlyConnectRelateWallInfo && equalsWallData(wallData, endOnlyConnectRelateWallInfo.wallData))
      ) {
        continue;
      }
      relateWallDataInfos.push({ wallData, connectPoint: Point2dUtils.clone(baseData.start) });
    } else if (mainWallSeg.pointOnSegment(baseData.end)) {
      if (
        (startOnlyConnectRelateWallInfo && equalsWallData(wallData, startOnlyConnectRelateWallInfo.wallData)) ||
        (endOnlyConnectRelateWallInfo && equalsWallData(wallData, endOnlyConnectRelateWallInfo.wallData))
      ) {
        continue;
      }
      relateWallDataInfos.push({ wallData, connectPoint: Point2dUtils.clone(baseData.end) });
    }
  }
  const specificConnectRelateWallInfo: SpecificConnectRelateWallInfo = {
    startOnlyConnectRelateWallInfo,
    endOnlyConnectRelateWallInfo,
    startLapWallDataInfos,
    endLapWallDataInfos,
    startConnectRelateWallInfos: startConnectWallDataInfos,
    endConnectRelateWallInfos: endConnectWallDataInfos,
    otherConnectRelateWallInfos: relateWallDataInfos
  };

  return specificConnectRelateWallInfo;
}

// 主墙体首尾会连接的墙体信息
export function connectStartEndRelateWallInfo(mainWallData: WallData, wallDatas: WallData[]) {
  const mainBaseData = mainWallData.baseData;
  const startConnectWalls: WallData[] = [];
  const endConnectWalls: WallData[] = [];
  for (const wallData of wallDatas) {
    if (equalsWallData(mainWallData, wallData)) {
      continue;
    }

    const { baseData } = wallData;
    const wallSeg = new LineSeg2d(Point2dUtils.clone(baseData.start), Point2dUtils.clone(baseData.end));
    if (wallSeg.pointOnSegment(mainBaseData.start)) {
      startConnectWalls.push(wallData);
    } else if (wallSeg.pointOnSegment(mainBaseData.end)) {
      endConnectWalls.push(wallData);
    }
  }

  return { startConnectWalls, endConnectWalls };
}

export function createWallData(wallData: WallData, start: IPoint2d, end: IPoint2d) {
  const baseData = {
    start,
    end,
    thickness: wallData.baseData.thickness,
    height: wallData.baseData.height,
    offGround: wallData.baseData.offGround
  };
  const drawWallInfo = initDrawWallInfo(baseData);
  const newWallData = {
    id: getUniqueId(),
    baseData: baseData,
    drawWallInfo: drawWallInfo
  };

  return newWallData;
}

export function isArcWallData(wallData: WallData): wallData is WallData & {
  baseData: Omit<WallBaseData, 'center' | 'clockwise'> & Required<Pick<WallBaseData, 'center' | 'clockwise'>>;
} {
  return wallData.baseData.center !== undefined && wallData.baseData.clockwise !== undefined;
}
